import React from "react";
import {Link, useLocation, Outlet, useNavigate} from "react-router-dom";
import "./Layout.css";
import {
  Box,
  CssBaseline,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Theme,
  CSSObject,
  styled,
  Tooltip,
  Divider,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import {ThemeProvider, createTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InventoryIcon from "@mui/icons-material/Inventory2";
import DiamondIcon from "@mui/icons-material/Diamond";
import BoltIcon from "@mui/icons-material/Bolt";
import StoreIcon from "@mui/icons-material/Store";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import pkg from "../../package.json";
import LogoutIcon from "@mui/icons-material/Logout";
import {useAppDispatch} from "../app/hooks";
import {logout} from "../app/features/authSlice";
import {resetProductState} from "../app/features/productSlice";
import {useProxyLogoutMutation} from "../app/services/auth";

const drawerWidth = 220;
const collapsedWidth = 64;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: collapsedWidth,
});

const Drawer = styled(MuiDrawer, {shouldForwardProp: prop => prop !== "open"})<{
  open?: boolean;
}>(({theme, open}) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const Layout: React.FC = () => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [mode, setMode] = React.useState<"light" | "dark">(() => {
    try {
      const saved = localStorage.getItem("tm.themeMode");
      if (saved === "light" || saved === "dark") return saved;
    } catch {}
    return "dark";
  });

  React.useEffect(() => {
    try {
      const saved = localStorage.getItem("tm.themeMode");
      if (!saved) setMode(prefersDarkMode ? "dark" : "light");
    } catch {}
  }, [prefersDarkMode]);

  React.useEffect(() => {
    try {
      localStorage.setItem("tm.themeMode", mode);
    } catch {}
  }, [mode]);

  const darkTheme = React.useMemo(() => createTheme({palette: {mode}}), [mode]);
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [pinned, setPinned] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem("tm.navPinned") === "1";
    } catch {
      return false;
    }
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [proxyLogout] = useProxyLogoutMutation();

  React.useEffect(() => {
    try {
      localStorage.setItem("tm.navPinned", pinned ? "1" : "0");
    } catch {}
    if (pinned) setOpen(true);
  }, [pinned]);

  const navItems = [
    {path: "/tm-dashboard", label: "Dashboard", icon: <DashboardIcon />},
    {path: "/products", label: "Products", icon: <InventoryIcon />},
    {
      path: "/subscriptions",
      label: "Subscription Tiers",
      icon: <DiamondIcon />,
    },
    {path: "/features", label: "Features", icon: <BoltIcon />},
    {path: "/dealers", label: "Dealers", icon: <StoreIcon />},
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    // Best-effort: revoke the refresh token and clear the httpOnly cookie server-side.
    try {
      await proxyLogout().unwrap();
    } catch {
      // Proceed with local logout even if the server call fails.
    }
    localStorage.removeItem("idms_email");
    dispatch(logout());
    dispatch(resetProductState());
    navigate("/login", {replace: true});
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          overflow: "hidden",
          bgcolor: "background.default",
        }}>
        <Drawer
          variant="permanent"
          open={open}
          onMouseEnter={() => {
            if (!pinned) setOpen(true);
          }}
          onMouseLeave={() => {
            if (!pinned) setOpen(false);
          }}
          sx={{height: "100vh"}}
          PaperProps={{sx: {borderRight: "1px solid", borderColor: "divider"}}}>
          <Box
            sx={{
              px: 1,
              py: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              justifyContent: open ? "space-between" : "center",
            }}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
              <img
                src="/target-logo.svg"
                alt="Target Manager Logo"
                width={32}
                height={32}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  opacity: open ? 1 : 0,
                }}>
                Target Manager
              </Typography>
            </Box>
            {open && (
              <Tooltip title={pinned ? "Unpin" : "Pin"}>
                <IconButton size="small" onClick={() => setPinned(v => !v)}>
                  {pinned ? (
                    <PushPinIcon fontSize="small" />
                  ) : (
                    <PushPinOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{px: 2, pb: 1, opacity: open ? 1 : 0}}>
            Dealer Management Platform
          </Typography>
          <Divider />
          <List disablePadding>
            {navItems.map(item => (
              <ListItem key={item.path} disablePadding sx={{display: "block"}}>
                <Tooltip
                  title={!open ? item.label : ""}
                  placement="right"
                  arrow>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={isActive(item.path)}
                    sx={{
                      minHeight: 44,
                      justifyContent: open ? "initial" : "center",
                      px: 2,
                      "&.Mui-selected": {
                        bgcolor: theme => theme.palette.action.selected,
                        color: "primary.main",
                        "& .MuiListItemIcon-root": {color: "primary.main"},
                      },
                    }}>
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1.5 : 0,
                        justifyContent: "center",
                      }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      sx={{opacity: open ? 1 : 0}}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
          <Box sx={{flexGrow: 1}} />
          <Divider />
          <Box sx={{px: 2, py: 1}}>
            <Typography variant="caption" color="text.secondary">
              v{pkg.version}
            </Typography>
          </Box>
        </Drawer>

        <Box
          component="main"
          sx={{flexGrow: 1, pt: 2, px: 1, overflow: "auto", minWidth: 0}}>
          <Box
            component="header"
            sx={{
              bgcolor: "background.paper",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}>
            <Box
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}>
              <Typography variant="h5" sx={{fontWeight: 600}}>
                {navItems.find(item => item.path === location.pathname)
                  ?.label || "Dashboard"}
              </Typography>

              <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                <Tooltip
                  title={
                    mode === "dark"
                      ? "Switch to light mode"
                      : "Switch to dark mode"
                  }>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setMode(m => (m === "dark" ? "light" : "dark"))
                    }
                    aria-label="toggle color mode">
                    {mode === "dark" ? (
                      <LightModeOutlinedIcon fontSize="small" />
                    ) : (
                      <DarkModeOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Logout">
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}>
                    Logout
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          </Box>
          <Box className="content-area" sx={{bgcolor: "background.default"}}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Layout;
